import { useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { HelpArticle, HelpArticleProposal, HelpDocPage, HelpMessage, HelpTicket, HelpTicketDetail, HelpTicketKind, HelpTicketStatus } from "@backstory/protocol";
import { EmptyListMark, EmptyState, ErrorState, Skeleton } from "../../components/States";
import { AutoLoadMore, BusyButton, ConfirmDialog, DataTable, Dialog, Field, PageHeader, RowMenu, SearchSelect, useToast, type Column } from "../../components/ui";
import { UnsavedGuard } from "../../components/UnsavedChanges";
import { AiOutput, AiSparkle } from "../ai/components/common";
import "../ai/ai.css";
import { formatDateTime, formatRelative } from "../../lib/format";
import { staffApi, staffKeys } from "../staff/api";
import { CustomerSelect, useDebounced } from "../staff/customers";
import { HelpMetrics, KindBadge, PAGE_SIZE, ThreadMessage, TicketSkeletonRows, TicketTitle, kindOptions, ticketMetrics, ticketTone } from "./shared";
import { ImagePicker, MessageImages, pastedImages, plainTextIssue, takeImages } from "./images";
import "./help.css";

const statusLabel: Record<HelpTicketStatus, string> = {
  open: "Open",
  waiting_on_us: "Waiting on us",
  waiting_on_them: "Waiting on customer",
  planned: "Planned",
  declined: "Won't build",
  resolved: "Resolved",
};
const statusOptions = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "open", label: "Open" },
  { value: "waiting_on_us", label: "Waiting on us" },
  { value: "waiting_on_them", label: "Waiting on customer" },
  { value: "planned", label: "Planned" },
  { value: "declined", label: "Won't build" },
  { value: "resolved", label: "Resolved" },
];
const kindChoices = kindOptions.filter((o) => o.value !== "");

/** Planned and won't-build belong to feature requests. A request already in one of those stays selectable so it can be moved. */
function statusChoices(kind: HelpTicketKind, current: HelpTicketStatus) {
  return statusOptions.filter((o) => {
    if (o.value === "all" || o.value === "active") return false;
    if (o.value !== "planned" && o.value !== "declined") return true;
    return kind === "feature" || o.value === current;
  });
}
const articleStatusLabel: Record<HelpArticle["status"], string> = {
  draft: "Draft",
  published: "Published",
  retired: "Retired",
};

function articleTone(status: HelpArticle["status"]) {
  return status === "published" ? "ok" : status === "retired" ? "neutral" : "warn";
}

function BackToQueue() {
  return (
    <Link to="/staff/help" className="btn ghost help-back" aria-label="Back to help desk">
      ←
    </Link>
  );
}

export function StaffHelpPage() {
  const [status, setStatus] = useState("all");
  const [kind, setKind] = useState("");
  const [orgId, setOrgId] = useState("");
  const [qInput, setQInput] = useState("");
  const q = useDebounced(qInput.trim(), 250);
  const filters = useMemo(() => ({ status, kind: kind || undefined, q: q || undefined, orgId: orgId || undefined }), [status, kind, q, orgId]);
  const filtered = !!(q || kind || orgId);
  const list = useInfiniteQuery({
    queryKey: staffKeys.help(filters),
    queryFn: ({ pageParam }) => staffApi.helpTickets({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.items.length, 0);
      return last.items.length === PAGE_SIZE && loaded < last.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });
  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];
  const total = list.data?.pages[0]?.total;
  const stats = useQuery({ queryKey: staffKeys.helpStats, queryFn: staffApi.helpStats });
  const metrics = useMemo(() => {
    const m = ticketMetrics(stats.data, "staff");
    m.splice(5, 0, { label: "Customers waiting", value: stats.data?.customersWaiting, tone: "warn", footer: "With a request waiting on us" });
    return m;
  }, [stats.data]);
  const clear = () => {
    setQInput("");
    setKind("");
    setOrgId("");
  };

  return (
    <div className="page help-page">
      <PageHeader
        title="Help desk"
        subtitle="Customer requests. Draft replies wait here until you send them."
        actions={<Link to="/staff/help/knowledge" className="btn">Knowledge base</Link>}
      >
        <div className="toolbar help-filters" aria-label="Help desk filters">
          <input
            className="input help-search"
            type="search"
            placeholder="Search requests or customers"
            aria-label="Search requests"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            autoComplete="off"
          />
          <SearchSelect value={status} options={statusOptions} onChange={setStatus} aria-label="Status" searchPlaceholder="Search statuses…" />
          <SearchSelect value={kind} options={kindOptions} onChange={setKind} aria-label="Type" searchPlaceholder="Search types…" />
          <CustomerSelect value={orgId} onChange={setOrgId} />
          {total !== undefined && <span className="muted small help-count">{total.toLocaleString()} {total === 1 ? "request" : "requests"}</span>}
        </div>
      </PageHeader>

      {!stats.isError && <HelpMetrics label="Help desk summary" metrics={metrics} loading={stats.isPending} activeStatus={status} onStatus={setStatus} />}

      {list.isError ? (
        <ErrorState error={list.error} retry={() => void list.refetch()} />
      ) : !list.isPending && rows.length === 0 ? (
        <div className="card">
          {filtered ? (
            <EmptyState
              mark={<EmptyListMark />}
              title="No requests match"
              hint="Try a different search or clear the filters."
              action={<button type="button" className="btn ghost" onClick={clear}>Clear filters</button>}
            />
          ) : (
            <EmptyState
              mark={<EmptyListMark />}
              title={status === "all" ? "No requests yet" : "Nothing in this queue"}
              hint={status === "all" ? "New customer requests show up here." : "Try another status."}
              action={status !== "all" ? <button type="button" className="btn ghost" onClick={() => setStatus("all")}>Show all requests</button> : undefined}
            />
          )}
        </div>
      ) : (
        <>
          <div className="card stack-table help-queue">
            <table className="table" aria-label="Help desk" aria-busy={list.isPending || list.isFetchingNextPage || undefined}>
              <thead>
                <tr>
                  <th className="help-col-request">Request</th>
                  <th className="help-col-customer">Customer</th>
                  <th className="help-col-status">Status</th>
                  <th className="help-col-updated">Updated</th>
                </tr>
              </thead>
              <tbody>
                {list.isPending ? <TicketSkeletonRows count={4} /> : rows.map((t) => <QueueRow key={t.id} ticket={t} />)}
                {list.isFetchingNextPage && <TicketSkeletonRows count={3} />}
              </tbody>
            </table>
          </div>
          <AutoLoadMore hasMore={!!list.hasNextPage} loading={list.isFetchingNextPage} loadMore={() => void list.fetchNextPage()} label="Loading more requests" />
        </>
      )}
    </div>
  );
}

function QueueRow({ ticket: t }: { ticket: HelpTicket }) {
  const navigate = useNavigate();
  const open = () => void navigate({ to: "/staff/help/$id", params: { id: t.id } });
  return (
    <tr
      className="clickable"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.target === e.currentTarget) open();
      }}
    >
      <td className="help-col-request" data-label="Request">
        <TicketTitle kind={t.kind} title={t.title} />
      </td>
      <td className="help-col-customer" data-label="Customer" title={t.reporterEmail}>
        <span className="help-ellipsis">{t.orgName || t.reporterEmail || "—"}</span>
      </td>
      <td className="help-col-status" data-label="Status">
        <span className={`badge ${ticketTone(t.status)}`}>{statusLabel[t.status]}</span>
      </td>
      <td className="help-col-updated muted" data-label="Updated" title={formatDateTime(t.updatedAt)}>
        {formatRelative(t.updatedAt)}
      </td>
    </tr>
  );
}

function Fact({ label, children, dirty = false }: { label: string; children: ReactNode; dirty?: boolean }) {
  return (
    <div className={`help-fact${dirty ? " dirty" : ""}`}>
      <dt>{label}{dirty && <span className="dirty-dot" title="Unsaved" />}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function authorOf(m: HelpMessage, t: HelpTicket) {
  if (m.authorKind === "customer") return { name: m.authorEmail || t.reporterEmail || "Customer", role: "Customer", tone: "customer" as const };
  if (m.authorKind === "staff") return { name: m.authorEmail || "Backstory", role: "Backstory staff", tone: "staff" as const };
  return { name: m.status === "draft" ? "Suggested reply" : "Backstory", role: m.status === "draft" ? "Written from the knowledge base" : "Reply from the knowledge base", tone: "ai" as const };
}

export function StaffHelpTicketPage({ id }: { id: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [images, setImages] = useState<File[]>([]);
  const [imageError, setImageError] = useState<string>();
  const textIssue = plainTextIssue(text);
  const [statusDraft, setStatusDraft] = useState<HelpTicketStatus | null>(null);
  const [kindDraft, setKindDraft] = useState<HelpTicketKind | null>(null);
  const [draftBody, setDraftBody] = useState<Record<string, string>>({});
  const [discarding, setDiscarding] = useState<string | null>(null);
  const [proposal, setProposal] = useState<HelpArticleProposal | null>(null);
  const ticket = useQuery({ queryKey: staffKeys.helpTicket(id), queryFn: () => staffApi.helpTicket(id) });
  const refresh = (detail: HelpTicketDetail) => {
    qc.setQueryData(staffKeys.helpTicket(id), detail);
    void qc.invalidateQueries({ queryKey: ["staff", "help"] });
  };
  const reply = useMutation({
    mutationFn: () => staffApi.helpReply(id, text.trim(), images),
    onSuccess: (d) => {
      refresh(d);
      setText("");
      setImages([]);
      setImageError(undefined);
      toast.push("success", "Reply sent");
    },
    onError: (e: Error) => toast.push("error", e.message),
  });
  const send = useMutation({
    mutationFn: (messageId: string) => staffApi.helpSendDraft(id, messageId, draftBody[messageId]),
    onSuccess: (d, messageId) => {
      refresh(d);
      setDraftBody(({ [messageId]: _, ...rest }) => rest);
      toast.push("success", "Reply sent");
    },
    onError: (e: Error) => toast.push("error", e.message),
  });
  const discard = useMutation({
    mutationFn: (messageId: string) => staffApi.helpDiscardDraft(id, messageId),
    onSuccess: (d, messageId) => {
      refresh(d);
      setDiscarding(null);
      setDraftBody(({ [messageId]: _, ...rest }) => rest);
    },
    onError: (e: Error) => toast.push("error", e.message),
  });
  const classify = useMutation({
    mutationFn: () => {
      const saved = ticket.data?.ticket;
      return staffApi.helpStatus(id, {
        status: statusDraft ?? saved!.status,
        kind: kindDraft ?? saved!.kind,
      });
    },
    onSuccess: (d) => {
      refresh(d);
      setStatusDraft(null);
      setKindDraft(null);
      toast.push("success", "Request updated");
    },
    onError: (e: Error) => toast.push("error", e.message),
  });
  const brief = useMutation({
    mutationFn: () => staffApi.helpBrief(id),
    onSuccess: refresh,
    onError: (e: Error) => toast.push("error", e.message),
  });
  const propose = useMutation({
    mutationFn: () => staffApi.helpProposeArticle(id),
    onSuccess: (r) => setProposal(r.proposal),
    onError: (e: Error) => toast.push("error", e.message),
  });
  const saveArticle = useMutation({
    mutationFn: (publish: boolean) => staffApi.helpSaveArticle({
      title: proposal!.title, question: proposal!.question, body: proposal!.body, area: proposal!.area || "general",
      status: publish ? "published" : "draft", sourceTicket: id,
    }),
    onSuccess: (_, publish) => {
      setProposal(null);
      toast.push("success", publish ? "Published to the knowledge base" : "Saved as a draft article");
      void qc.invalidateQueries({ queryKey: staffKeys.helpArticles });
    },
    onError: (e: Error) => toast.push("error", e.message),
  });

  if (ticket.isError) {
    return (
      <div className="page help-page">
        <PageHeader back={<BackToQueue />} title="Request" />
        <ErrorState error={ticket.error} retry={() => void ticket.refetch()} />
      </div>
    );
  }
  const detail = ticket.data;
  if (!detail) {
    return (
      <div className="page help-page">
        <PageHeader back={<BackToQueue />} title="Loading request…" />
        <Skeleton lines={8} />
      </div>
    );
  }
  const t = detail.ticket;
  const statusValue = statusDraft ?? t.status;
  const kindValue = kindDraft ?? t.kind;
  const classificationDirty = statusValue !== t.status || kindValue !== t.kind;
  const drafts = detail.messages.filter((m) => m.status === "draft");
  const draftsDirty = drafts.some((m) => draftBody[m.id] !== undefined && draftBody[m.id] !== m.body);
  const proposalValid = !!proposal?.title.trim() && !!proposal?.question.trim() && !!proposal?.body.trim()
    && !plainTextIssue(proposal.title) && !plainTextIssue(proposal.question) && !plainTextIssue(proposal.body) && !plainTextIssue(proposal.area);
  const opening = detail.messages.find((m) => m.authorKind === "customer" && m.status !== "draft");
  const replies = detail.messages.filter((m) => m.id !== opening?.id);
  const renderMessage = (m: HelpMessage) => {
    const who = authorOf(m, t);
    const isDraft = m.status === "draft";
    return (
      <ThreadMessage
        key={m.id}
        name={who.name}
        role={who.role}
        tone={who.tone}
        at={m.createdAt}
        draft={isDraft}
        badge={isDraft ? <span className="badge warn">Not sent</span> : undefined}
        citations={m.citations}
        footer={
          isDraft ? (
            <>
              <button type="button" className="btn ghost" onClick={() => setDiscarding(m.id)}>Discard</button>
              <BusyButton
                className="btn primary"
                busy={send.isPending && send.variables === m.id}
                busyLabel="Sending…"
                disabled={!(draftBody[m.id] ?? m.body).trim() || !!plainTextIssue(draftBody[m.id] ?? m.body) || send.isPending}
                onClick={() => send.mutate(m.id)}
              >
                Send to customer
              </BusyButton>
            </>
          ) : undefined
        }
      >
        {isDraft ? (
          <>
            <textarea
              className="input"
              aria-label="Suggested reply"
              autoComplete="off"
              rows={6}
              value={draftBody[m.id] ?? m.body}
              onChange={(e) => setDraftBody((d) => ({ ...d, [m.id]: e.target.value }))}
            />
            {plainTextIssue(draftBody[m.id] ?? m.body) && <p className="field-error" role="alert">{plainTextIssue(draftBody[m.id] ?? m.body)}</p>}
          </>
        ) : (
          <>
            <p>{m.body}</p>
            <MessageImages paths={(m.attachments ?? []).map((a) => `/v1/staff/help/tickets/${encodeURIComponent(id)}/attachments/${encodeURIComponent(a.id)}`)} />
          </>
        )}
      </ThreadMessage>
    );
  };

  return (
    <div className="page help-page help-ticket">
      <UnsavedGuard dirty={!!text.trim() || images.length > 0 || draftsDirty || classificationDirty} />
      <PageHeader
        back={<BackToQueue />}
        title={t.title}
        subtitle={
          <span className="help-subtitle">
            <KindBadge kind={t.kind} />
            <span className={`badge ${ticketTone(t.status)}`}>{statusLabel[t.status]}</span>
            <span>{t.orgName || t.reporterEmail}</span>
            <span title={formatDateTime(t.createdAt)}>Opened {formatRelative(t.createdAt)}</span>
          </span>
        }
        actions={
          <button type="button" className="btn" onClick={() => propose.mutate()} disabled={propose.isPending}>
            {propose.isPending ? "Drafting…" : "Add to knowledge base"}
          </button>
        }
      />

      <div className="help-ticket-grid">
        <aside className="help-side">
          <section className="card help-panel" aria-label="Request details">
            <h2>Details</h2>
            <dl className="help-facts">
              <Fact label="Type" dirty={kindValue !== t.kind}>
                <SearchSelect
                  value={kindValue}
                  options={kindChoices}
                  onChange={(v) => {
                    const next = v as HelpTicketKind;
                    setKindDraft(next);
                    if (next !== "feature" && (statusValue === "planned" || statusValue === "declined")) setStatusDraft("open");
                  }}
                  aria-label="Request type"
                  searchPlaceholder="Search types…"
                  disabled={classify.isPending}
                />
              </Fact>
              <Fact label="Status" dirty={statusValue !== t.status}>
                <SearchSelect
                  value={statusValue}
                  options={statusChoices(kindValue, statusValue)}
                  onChange={(v) => setStatusDraft(v as HelpTicketStatus)}
                  aria-label="Status"
                  searchPlaceholder="Search statuses…"
                  disabled={classify.isPending}
                />
              </Fact>
              <Fact label="Customer">
                <Link to="/staff/orgs/$org" params={{ org: t.orgId }} className="help-link">{t.orgName || t.orgId}</Link>
              </Fact>
              <Fact label="Reported by">
                <span className="help-wrap">{t.reporterEmail || "—"}</span>
                {t.reporterRole && <span className="muted small"> · {t.reporterRole}</span>}
              </Fact>
              {t.pagePath && <Fact label="Page"><span className="mono small help-wrap">{t.pagePath}</span></Fact>}
              {t.appVersion && <Fact label="App version"><span className="mono small">{t.appVersion}</span></Fact>}
              {t.sessionId && (
                <Fact label="Session">
                  <Link to="/staff/orgs/$org/sessions/$id" params={{ org: t.orgId, id: t.sessionId }} className="help-link">Watch the session</Link>
                </Fact>
              )}
              <Fact label="Last update"><span title={formatDateTime(t.updatedAt)}>{formatRelative(t.updatedAt)}</span></Fact>
            </dl>
            <div className="help-classify">
              {classificationDirty && <span className="dirty-note" role="status"><strong>Unsaved changes</strong></span>}
              {classificationDirty && (
                <button type="button" className="btn" disabled={classify.isPending} onClick={() => { setStatusDraft(null); setKindDraft(null); }}>Discard</button>
              )}
              <BusyButton className="btn primary" busy={classify.isPending} busyLabel="Saving…" disabled={!classificationDirty} onClick={() => classify.mutate()}>
                Save
              </BusyButton>
            </div>
          </section>
        </aside>

        <div className="help-main">
          <ol className="help-thread" aria-label="Request">
            {opening && renderMessage(opening)}
          </ol>

          <section className="card help-panel help-brief" aria-label="Internal brief">
            <div className="help-panel-head">
              <h2>Internal brief</h2>
              <button type="button" className="btn sm ai-btn" onClick={() => brief.mutate()} disabled={brief.isPending || t.briefStatus === "pending"}>
                <AiSparkle size={12} />
                {brief.isPending || t.briefStatus === "pending" ? "Writing…" : t.brief ? "Rewrite" : "Write brief"}
              </button>
            </div>
            {t.briefStatus === "failed" && <p className="banner danger" role="alert">{t.briefError || "The brief could not be written."}</p>}
            {t.brief ? (
              <AiOutput className="help-brief-output" testId="help-brief">
                <p>{t.brief.summary}</p>
                <BriefList title="What we can do" items={t.brief.canDo} />
                <BriefList title="What we cannot do" items={t.brief.cannotDo} />
                {t.brief.likelyCause && (
                  <section>
                    <h4>Likely cause</h4>
                    <p>{t.brief.likelyCause}</p>
                  </section>
                )}
                {t.brief.files && t.brief.files.length > 0 && (
                  <section>
                    <h4>Where to look</h4>
                    <ul className="help-files">
                      {t.brief.files.map((f) => <li key={f} className="mono small">{f}</li>)}
                    </ul>
                  </section>
                )}
                <span className={`badge ${t.brief.covered ? "ok" : "warn"}`}>
                  {t.brief.covered ? "Covered by the knowledge base" : "Not covered by the knowledge base"}
                </span>
              </AiOutput>
            ) : t.briefStatus === "pending" ? (
              <p className="muted">Writing the brief…</p>
            ) : t.briefStatus !== "failed" ? (
              <p className="muted">No brief yet.</p>
            ) : null}
          </section>

          {replies.length > 0 && (
            <ol className="help-thread" aria-label="Replies">
              {replies.map(renderMessage)}
            </ol>
          )}

          <form
            className="card help-reply"
            onSubmit={(e) => {
              e.preventDefault();
              if (text.trim() && !textIssue && !imageError && !reply.isPending) reply.mutate();
            }}
          >
            <Field label="Reply to the customer" hint="The customer sees this on their Help page and gets it by email." error={textIssue}>
              {(p) => (
                <textarea
                  {...p}
                  className="input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onPaste={(e) => {
                    const pasted = pastedImages(e);
                    if (pasted.length === 0) return;
                    e.preventDefault();
                    const next = takeImages(images, pasted);
                    setImages(next.files);
                    setImageError(next.error);
                  }}
                  rows={5}
                  placeholder="Write a reply…"
                />
              )}
            </Field>
            <ImagePicker files={images} error={imageError} onChange={(next, err) => { setImages(next); setImageError(err); }}>
              <BusyButton className="btn primary" type="submit" busy={reply.isPending} busyLabel="Sending…" disabled={!text.trim() || !!textIssue || !!imageError}>Send reply</BusyButton>
            </ImagePicker>
          </form>
        </div>
      </div>

      <ConfirmDialog
        open={!!discarding}
        title="Discard suggested reply"
        confirmLabel="Discard reply"
        danger
        busy={discard.isPending}
        onConfirm={() => discarding && discard.mutate(discarding)}
        onClose={() => setDiscarding(null)}
      >
        The suggested reply on “{t.title}” will be deleted. The customer never saw it.
      </ConfirmDialog>

      <Dialog
        open={!!proposal}
        onClose={() => setProposal(null)}
        title="New knowledge base article"
        subtitle="Written from this request. Published articles can be quoted in replies."
        className="help-editor"
        side
        footer={
          <>
            <button type="button" className="btn ghost" onClick={() => setProposal(null)}>Cancel</button>
            <button type="button" className="btn" onClick={() => saveArticle.mutate(false)} disabled={!proposalValid || saveArticle.isPending}>Save as draft</button>
            <BusyButton className="btn primary" busy={saveArticle.isPending} busyLabel="Saving…" disabled={!proposalValid} onClick={() => saveArticle.mutate(true)}>Publish</BusyButton>
          </>
        }
      >
        {proposal && (
          <div className="help-editor-fields">
            <Field label="Title" error={plainTextIssue(proposal.title)}>
              {(p) => <input {...p} className="input" value={proposal.title} onChange={(e) => setProposal({ ...proposal, title: e.target.value })} required />}
            </Field>
            <Field label="Question" error={plainTextIssue(proposal.question)}>
              {(p) => <input {...p} className="input" value={proposal.question} onChange={(e) => setProposal({ ...proposal, question: e.target.value })} required />}
            </Field>
            <Field label="Answer" error={plainTextIssue(proposal.body)}>
              {(p) => <textarea {...p} className="input" rows={8} value={proposal.body} onChange={(e) => setProposal({ ...proposal, body: e.target.value })} required />}
            </Field>
            <Field label="Area" hint="A short product area, such as recording or billing." error={plainTextIssue(proposal.area)}>
              {(p) => <input {...p} className="input" value={proposal.area} onChange={(e) => setProposal({ ...proposal, area: e.target.value })} />}
            </Field>
          </div>
        )}
      </Dialog>
    </div>
  );
}

function BriefList({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <section>
      <h4>{title}</h4>
      <ul>
        {items.map((s, i) => <li key={i}>{s}</li>)}
      </ul>
    </section>
  );
}

type ArticleDraft = Partial<HelpArticle>;

const articleFilterOptions = [
  { value: "all", label: "All articles" },
  { value: "published", label: "Published" },
  { value: "draft", label: "Draft" },
  { value: "retired", label: "Retired" },
];

const articleColumns: Column<HelpArticle>[] = [
  {
    key: "article",
    header: "Article",
    label: "Article",
    className: "help-col-request",
    render: (a) => (
      <span className="help-cell">
        <strong className="help-ellipsis" title={a.title}>{a.title}</strong>
        <span className="muted small help-ellipsis" title={a.question}>{a.question}</span>
      </span>
    ),
  },
  { key: "status", header: "Status", label: "Status", className: "help-col-status", render: (a) => <span className={`badge ${articleTone(a.status)}`}>{articleStatusLabel[a.status]}</span> },
  { key: "area", header: "Area", label: "Area", className: "help-col-area", render: (a) => a.area || "—" },
  { key: "used", header: "Last quoted", label: "Last quoted", className: "help-col-updated", render: (a) => <span className="muted">{a.lastUsedAt ? `Quoted ${formatRelative(a.lastUsedAt)}` : "Never quoted"}</span> },
  { key: "actions", header: "", className: "help-col-actions", render: () => null },
];

const docColumns: Column<HelpDocPage>[] = [
  {
    key: "page",
    header: "Page",
    label: "Page",
    className: "help-col-request",
    render: (p) => (
      <span className="help-title-line">
        <strong className="help-ellipsis" title={p.title}>{p.title}</strong>
        {p.excluded && <span className="badge neutral">Excluded</span>}
      </span>
    ),
  },
  { key: "url", header: "Address", label: "Address", className: "help-col-url", render: (p) => <span className="muted help-ellipsis" title={p.url}>{p.url.replace(/^https?:\/\//, "")}</span> },
  { key: "actions", header: "", className: "help-col-actions", render: () => null },
];

function articleDirty(editing: ArticleDraft, saved: ArticleDraft | null) {
  const fields = ["title", "question", "body", "area"] as const;
  return fields.some((k) => (editing[k] || "") !== (saved?.[k] || ""));
}

export function StaffKnowledgePage() {
  const toast = useToast();
  const qc = useQueryClient();
  const articles = useQuery({ queryKey: staffKeys.helpArticles, queryFn: () => staffApi.helpArticles() });
  const docs = useQuery({ queryKey: staffKeys.helpDocs, queryFn: () => staffApi.helpDocs() });
  const [editing, setEditing] = useState<ArticleDraft | null>(null);
  const [saved, setSaved] = useState<ArticleDraft | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [retire, setRetire] = useState<HelpArticle | null>(null);
  const [qInput, setQInput] = useState("");
  const q = useDebounced(qInput.trim(), 200);
  const [articleFilter, setArticleFilter] = useState("all");
  const openEditor = (article: ArticleDraft) => {
    setEditing(article);
    setSaved(article);
  };
  const closeEditor = () => {
    if (editing && articleDirty(editing, saved)) setDiscardOpen(true);
    else setEditing(null);
  };
  const save = useMutation({
    mutationFn: () => staffApi.helpSaveArticle({
      id: editing?.id,
      title: editing?.title || "",
      question: editing?.question || "",
      body: editing?.body || "",
      area: editing?.area || "general",
      status: editing?.status || "draft",
    }),
    onSuccess: () => {
      setEditing(null);
      void qc.invalidateQueries({ queryKey: staffKeys.helpArticles });
    },
    onError: (e: Error) => toast.push("error", e.message),
  });
  const setStatus = useMutation({
    mutationFn: (v: { id: string; status: HelpArticle["status"] }) => staffApi.helpArticleStatus(v.id, v.status),
    onSuccess: () => {
      setRetire(null);
      void qc.invalidateQueries({ queryKey: staffKeys.helpArticles });
    },
  });
  const reindex = useMutation({
    mutationFn: () => staffApi.helpReindex(),
    onSuccess: (r) => {
      qc.setQueryData(staffKeys.helpDocs, { items: r.items, docsUrl: docs.data?.docsUrl ?? "" });
      toast.push("success", `Indexed ${r.indexed} docs pages`);
    },
    onError: (e: Error) => toast.push("error", e.message),
  });
  const exclude = useMutation({
    mutationFn: (v: { id: string; excluded: boolean }) => staffApi.helpExcludeDoc(v.id, v.excluded),
    onSuccess: () => void qc.invalidateQueries({ queryKey: staffKeys.helpDocs }),
  });
  const allArticles = articles.data?.items ?? [];
  const allDocs = docs.data?.items ?? [];
  const needle = q.toLowerCase();
  const rows = allArticles.filter((a) =>
    (articleFilter === "all" || a.status === articleFilter) &&
    (!needle || `${a.title} ${a.question} ${a.body} ${a.area}`.toLowerCase().includes(needle)),
  );
  const docRows = allDocs.filter((p) => !needle || `${p.title} ${p.url}`.toLowerCase().includes(needle));
  const filtered = !!needle || articleFilter !== "all";
  const clear = () => {
    setQInput("");
    setArticleFilter("all");
  };
  const canSave = !!editing?.title?.trim() && !!editing?.question?.trim() && !!editing?.body?.trim() && articleDirty(editing, saved)
    && !plainTextIssue(editing.title || "") && !plainTextIssue(editing.question || "") && !plainTextIssue(editing.body || "") && !plainTextIssue(editing.area || "");
  return (
    <div className="page help-page">
      <PageHeader
        back={<BackToQueue />}
        title="Knowledge base"
        subtitle="Published articles and indexed docs pages are the only sources a customer reply may use."
        actions={<button type="button" className="btn primary" onClick={() => openEditor({ title: "", question: "", body: "", area: "general", status: "draft" })}>New article</button>}
      >
        <div className="toolbar help-filters help-kb-filters" aria-label="Knowledge base filters">
          <input
            className="input help-search"
            type="search"
            placeholder="Search articles and docs pages"
            aria-label="Search the knowledge base"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            autoComplete="off"
          />
          <SearchSelect value={articleFilter} options={articleFilterOptions} onChange={setArticleFilter} aria-label="Article status" searchPlaceholder="Search statuses…" />
        </div>
      </PageHeader>
      <div className="help-section-head">
        <div>
          <h2>Articles</h2>
          <p className="muted">Written by staff. Only published articles are quoted in replies.</p>
        </div>
      </div>
      {articles.isError ? (
        <ErrorState error={articles.error} retry={() => void articles.refetch()} />
      ) : articles.isPending ? (
        <div className="card stack-table help-articles">
          <DataTable columns={articleColumns} rows={[]} rowKey={(a) => a.id} loading ariaLabel="Articles" />
        </div>
      ) : allArticles.length === 0 ? (
        <div className="card">
          <EmptyState mark={<EmptyListMark />} title="No articles yet" hint="A published article is something a customer reply is allowed to quote." />
        </div>
      ) : rows.length === 0 ? (
        <div className="card">
          <EmptyState
            mark={<EmptyListMark />}
            title="No articles match"
            hint="Try a different search or status."
            action={filtered ? <button type="button" className="btn ghost" onClick={clear}>Clear filters</button> : undefined}
          />
        </div>
      ) : (
        <div className="card stack-table help-articles">
          <DataTable
            ariaLabel="Articles"
            rows={rows}
            rowKey={(a) => a.id}
            onRowClick={(a) => openEditor(a)}
            columns={[
              ...articleColumns.slice(0, -1),
              {
                key: "actions",
                header: "",
                className: "help-col-actions",
                render: (a) => (
                  <span onClick={(e) => e.stopPropagation()}>
                    <RowMenu label={`Actions for ${a.title}`}>
                      <button type="button" role="menuitem" onClick={() => openEditor(a)}>Edit</button>
                      {a.status !== "published" && <button type="button" role="menuitem" onClick={() => setStatus.mutate({ id: a.id, status: "published" })}>Publish</button>}
                      {a.status === "published" && <button type="button" role="menuitem" onClick={() => setStatus.mutate({ id: a.id, status: "draft" })}>Unpublish</button>}
                      {a.status !== "retired" && <button type="button" role="menuitem" className="danger" onClick={() => setRetire(a)}>Retire</button>}
                    </RowMenu>
                  </span>
                ),
              },
            ]}
          />
        </div>
      )}
      <section className="help-docs-section">
        <div className="help-section-head">
          <div>
            <h2>Docs pages</h2>
            <p className="muted">These are the public pages a reply may link. Excluding a page keeps it on the docs site and out of replies.</p>
          </div>
          <BusyButton className="btn" busy={reindex.isPending} busyLabel="Rebuilding…" onClick={() => reindex.mutate()}>Rebuild index</BusyButton>
        </div>
        {docs.isError ? (
          <ErrorState error={docs.error} retry={() => void docs.refetch()} />
        ) : docs.isPending ? (
          <div className="card stack-table help-docs">
            <DataTable columns={docColumns} rows={[]} rowKey={(p) => p.id} loading ariaLabel="Docs pages" />
          </div>
        ) : allDocs.length === 0 ? (
          <div className="card">
            <EmptyState mark={<EmptyListMark />} title="No pages indexed yet" hint="Rebuild the index from the docs site." />
          </div>
        ) : docRows.length === 0 ? (
          <div className="card">
            <EmptyState mark={<EmptyListMark />} title="No docs pages match" hint="Try a different search." action={<button type="button" className="btn ghost" onClick={clear}>Clear search</button>} />
          </div>
        ) : (
          <div className="card stack-table help-docs">
            <DataTable
              ariaLabel="Docs pages"
              rows={docRows}
              rowKey={(p) => p.id}
              onRowClick={(p) => window.open(p.url, "_blank", "noopener,noreferrer")}
              columns={[
                ...docColumns.slice(0, -1),
                {
                  key: "actions",
                  header: "",
                  className: "help-col-actions",
                  render: (p) => (
                    <span onClick={(e) => e.stopPropagation()}>
                      <RowMenu label={`Actions for ${p.title}`}>
                        <button type="button" role="menuitem" onClick={() => window.open(p.url, "_blank", "noopener,noreferrer")}>Open page</button>
                        <button type="button" role="menuitem" onClick={() => exclude.mutate({ id: p.id, excluded: !p.excluded })}>
                          {p.excluded ? "Include in replies" : "Exclude from replies"}
                        </button>
                      </RowMenu>
                    </span>
                  ),
                },
              ]}
            />
          </div>
        )}
      </section>
      <Dialog
        open={!!editing}
        onClose={closeEditor}
        title={editing?.id ? "Edit article" : "New article"}
        className="help-editor"
        side
        footer={
          <>
            <button type="button" className="btn ghost" onClick={closeEditor}>Cancel</button>
            <BusyButton className="btn primary" busy={save.isPending} busyLabel="Saving…" disabled={!canSave} onClick={() => save.mutate()}>Save</BusyButton>
          </>
        }
      >
        {editing && (
          <div className="help-editor-fields">
            <Field label="Title" error={plainTextIssue(editing.title || "")}>
              {(p) => <input {...p} className="input" value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} required />}
            </Field>
            <Field label="Question" error={plainTextIssue(editing.question || "")}>
              {(p) => <input {...p} className="input" value={editing.question || ""} onChange={(e) => setEditing({ ...editing, question: e.target.value })} required />}
            </Field>
            <Field label="Answer" error={plainTextIssue(editing.body || "")}>
              {(p) => <textarea {...p} className="input" rows={8} value={editing.body || ""} onChange={(e) => setEditing({ ...editing, body: e.target.value })} required />}
            </Field>
            <Field label="Area" hint="A short product area, such as recording or billing." error={plainTextIssue(editing.area || "")}>
              {(p) => <input {...p} className="input" value={editing.area || ""} onChange={(e) => setEditing({ ...editing, area: e.target.value })} />}
            </Field>
          </div>
        )}
      </Dialog>
      <ConfirmDialog
        open={discardOpen}
        title="Discard article changes"
        confirmLabel="Discard changes"
        danger
        typeToConfirm={false}
        onConfirm={() => { setDiscardOpen(false); setEditing(null); }}
        onClose={() => setDiscardOpen(false)}
      >
        This article has changes that are not saved.
      </ConfirmDialog>
      <ConfirmDialog
        open={!!retire}
        title="Retire article"
        confirmLabel="Retire article"
        danger
        busy={setStatus.isPending}
        onConfirm={() => retire && setStatus.mutate({ id: retire.id, status: "retired" })}
        onClose={() => setRetire(null)}
      >
        {retire ? `“${retire.title}” will no longer be used in customer replies.` : null}
      </ConfirmDialog>
    </div>
  );
}
